/**
 * /market/:slug/personalizado — Configurador de estampados/bordados.
 * Permite al cliente configurar su pedido y enviar solicitud de cotización.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { getRequest } from "@tanstack/react-start/server";
import { loadMarketCatalog } from "@/lib/market-catalog";
import {
  dbSmListGarmentTypes,
  dbSmListAllZones,
  dbSmListWorkTypes,
  dbSmGetMerchantConfig,
} from "@/lib/db-service";
import type { MerchantTenantConfig, GarmentType, Zone, WorkType } from "@/lib/db-service/merchant";
import type { MarketCatalogData } from "@/lib/market-catalog";
import { StructuraPlatformSignature } from "@/components/StructuraPlatformSignature";
import { QuoteConfigurator } from "@/components/merchant/QuoteConfigurator";
import type { QuoteSubmitData, MerchantBaseProduct } from "@/components/merchant/QuoteConfigurator";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";

// ─── SERVER FN ────────────────────────────────────────────────────────────────

interface MerchantPageData {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  primaryColor: string | null;
  logoUrl: string | null;
  products: MerchantBaseProduct[];
  garmentTypes: GarmentType[];
  zones: Zone[];
  workTypes: WorkType[];
  merchantConfig: MerchantTenantConfig | null;
}

const fetchMerchantPageData = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  .handler(async ({ data: slug }): Promise<MerchantPageData> => {
    const catalogResult = await loadMarketCatalog(slug, { request: getRequest() });

    let catalogData: MarketCatalogData | null = null;
    if (catalogResult.ok) {
      catalogData = catalogResult.data;
    }

    const tenantId = catalogData?.tenant?.id ?? "";

    const [garmentTypes, zones, workTypes, merchantConfig] = await Promise.all([
      dbSmListGarmentTypes(),
      dbSmListAllZones(),
      dbSmListWorkTypes(),
      tenantId ? dbSmGetMerchantConfig(tenantId) : Promise.resolve(null),
    ]);

    // Mapear productos del catálogo a MerchantBaseProduct
    const products: MerchantBaseProduct[] = (catalogData?.products ?? [])
      .filter((p) => p.activo !== false)
      .map((p) => ({
        id: String(p.id),
        name: String(p.name),
        imageUrl: p.imagenUrl ?? null,
        price: p.precioUnitario ?? null,
        garmentTypeId: null,
      }));

    return {
      tenantId,
      tenantName: catalogData?.tenant?.name ?? slug,
      tenantSlug: slug,
      primaryColor: catalogData?.config?.primaryColor ?? null,
      logoUrl: catalogData?.tenant?.logoUrl ?? null,
      products,
      garmentTypes,
      zones,
      workTypes,
      merchantConfig,
    };
  });

// ─── ROUTE ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute("/market_/$slug/personalizado")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchMerchantPageData({ data: params.slug }),
  component: PersonalizadoPage,
  head: ({ params }) => ({
    meta: [{ title: `Personalizado — ${params.slug}` }],
  }),
});

// ─── PAGE ─────────────────────────────────────────────────────────────────────

function PersonalizadoPage() {
  const data = Route.useLoaderData() as MerchantPageData;
  const {
    tenantId,
    tenantName,
    tenantSlug,
    primaryColor,
    logoUrl,
    products,
    garmentTypes,
    zones,
    workTypes,
    merchantConfig,
  } = data;

  const [successQuoteId, setSuccessQuoteId] = useState<string | null>(null);

  const enabledZones = merchantConfig?.enabledZones ?? zones.map((z: Zone) => z.id);
  const enabledWorkTypes = merchantConfig?.enabledWorkTypes ?? workTypes.map((w: WorkType) => w.id);

  const color = primaryColor ?? "#2563eb";

  async function handleSubmit(submitData: QuoteSubmitData) {
    const res = await fetch("/api/merchant/quotes", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Tenant-Id": tenantId,
      },
      body: JSON.stringify({
        customerName: submitData.customerName,
        customerEmail: submitData.customerEmail,
        customerPhone: submitData.customerPhone,
        productId: submitData.productId,
        productName: submitData.productName,
        quantity: submitData.quantity,
        notes: submitData.notes,
        items: submitData.items,
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Error al enviar" }));
      throw new Error((err as { error: string }).error ?? "Error al enviar la solicitud");
    }

    const quote = await res.json() as { id: string };
    setSuccessQuoteId(quote.id);
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Header */}
      <header
        className="sticky top-0 z-30 border-b border-black/10"
        style={{ background: color }}
      >
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link
            to="/market/$slug"
            params={{ slug: tenantSlug }}
            className="flex items-center gap-1 text-white/80 hover:text-white text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Link>
          <div className="flex-1 flex items-center gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt={tenantName} className="h-7 object-contain" />
            ) : (
              <span className="font-bold text-white text-lg">{tenantName}</span>
            )}
          </div>
          <span className="text-white/70 text-sm">Personalizado</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {successQuoteId ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">¡Solicitud enviada!</h1>
            <p className="text-gray-500 mb-2 max-w-md mx-auto">
              Recibimos tu solicitud de cotización. Revisá tu email — te enviaremos la cotización a la brevedad.
            </p>
            <p className="text-xs text-gray-400 mb-8">
              N.° de solicitud: <span className="font-mono">{successQuoteId}</span>
            </p>
            <Link
              to="/market/$slug"
              params={{ slug: tenantSlug }}
              className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver a la tienda
            </Link>
          </div>
        ) : (
          <QuoteConfigurator
            tenantSlug={tenantSlug}
            products={products}
            garmentTypes={garmentTypes}
            zones={zones}
            workTypes={workTypes}
            enabledZones={enabledZones}
            enabledWorkTypes={enabledWorkTypes}
            onSubmit={handleSubmit}
          />
        )}
      </main>

      <footer className="py-6 text-center">
        <StructuraPlatformSignature />
      </footer>
    </div>
  );
}
